import { useCallback, useEffect, useRef, useState } from 'react';
import { resumeService } from '../../services/resumeService';
import {
  emptyWizardState,
  buildSectionContent,
  nonEmptySections,
  fromParsedSections,
} from './sectionShapes';

const DRAFT_KEY = 'jobx_resume_draft';

export const readDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearDraft = () => localStorage.removeItem(DRAFT_KEY);

const friendlyError = (err, fallback) => {
  if (err?.response?.status === 429) return 'AI is busy right now — please try again in a moment.';
  return err?.response?.data?.detail || fallback;
};

export default function useResumeSession() {
  const [sessionId, setSessionId] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [wizardState, setWizardState] = useState(emptyWizardState);
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [expired, setExpired] = useState(false);
  const draftTimer = useRef(null);

  // Debounced draft mirror so a refresh never loses typed data.
  useEffect(() => {
    if (!sessionId) return;
    clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({ templateId, wizardState, stepIndex, savedAt: Date.now() })
        );
      } catch {
        /* storage full — draft mirroring is best-effort */
      }
    }, 400);
    return () => clearTimeout(draftTimer.current);
  }, [sessionId, templateId, wizardState, stepIndex]);

  const startFresh = useCallback(async (tplId, initialState = null, initialStep = 0) => {
    const session = await resumeService.startSession(tplId);
    const id = session.session_id || session.id;
    if (!id) throw new Error('No session id returned');
    setSessionId(id);
    setTemplateId(tplId);
    setWizardState(initialState || emptyWizardState());
    setStepIndex(initialStep);
    setProgress(0);
    setExpired(false);
    setSaveError(null);
    return id;
  }, []);

  const resumeFromUpload = useCallback(async (file) => {
    const result = await resumeService.uploadResume(file);
    setSessionId(result.session_id);
    setTemplateId(null);
    const parsedObj = result.parsed_data || result.parsed_sections;
    setWizardState(fromParsedSections(parsedObj));
    setStepIndex(0);
    setExpired(false);
    setSaveError(null);
    return result;
  }, []);

  const resumeDraft = useCallback(async () => {
    const draft = readDraft();
    if (!draft) return false;
    // Backend sessions expire; start a fresh one and reuse the locally saved answers.
    await startFresh(draft.templateId || 1, draft.wizardState, draft.stepIndex || 0);
    return true;
  }, [startFresh]);

  const refreshProgress = useCallback(async (id) => {
    try {
      const p = await resumeService.getProgress(id);
      if (typeof p?.progress === 'number') setProgress(p.progress);
    } catch {
      /* progress is cosmetic — ignore failures */
    }
  }, []);

  const saveSection = useCallback(
    async (sectionType, { isAiEnhanced = false } = {}) => {
      if (!sessionId) return false;
      setSaving(true);
      setSaveError(null);
      try {
        const content = buildSectionContent(sectionType, wizardState);
        await resumeService.submitSection(sessionId, sectionType, content, { isAiEnhanced });
        refreshProgress(sessionId);
        return true;
      } catch (err) {
        if ([404, 410].includes(err?.response?.status)) {
          setExpired(true);
        }
        setSaveError(friendlyError(err, "Couldn't save this step. Check your connection and retry."));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [sessionId, wizardState, refreshProgress]
  );

  // Start a new session and replay everything already filled in.
  const restartSession = useCallback(async () => {
    const session = await resumeService.startSession(templateId || 1);
    const id = session.session_id || session.id;
    setSessionId(id);
    setExpired(false);
    setSaveError(null);
    for (const sectionType of nonEmptySections(wizardState)) {
      try {
        await resumeService.submitSection(id, sectionType, buildSectionContent(sectionType, wizardState));
      } catch {
        /* individual replays are retried when the user hits Next on that step */
      }
    }
    refreshProgress(id);
    return id;
  }, [templateId, wizardState, refreshProgress]);

  const generatePdf = useCallback(
    async (title) => {
      await resumeService.generatePDF(sessionId, templateId || 1, title || 'My Resume');
      const objectUrl = await resumeService.downloadResumePdf(sessionId);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'jobxbharat_resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      clearDraft();
    },
    [sessionId, templateId]
  );

  return {
    sessionId,
    templateId,
    setTemplateId,
    wizardState,
    setWizardState,
    stepIndex,
    setStepIndex,
    progress,
    saving,
    saveError,
    setSaveError,
    expired,
    startFresh,
    resumeFromUpload,
    resumeDraft,
    saveSection,
    restartSession,
    generatePdf,
  };
}
